import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { images } from "@/config/content";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal, Overline } from "@/components/Reveal";

function ParallaxImage({ src, alt, amount = 40 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-amount, amount]);
  return (
    <div ref={ref} className="relative h-full overflow-hidden rounded-xl border border-edge">
      <motion.img
        style={{ y }}
        src={src}
        alt={alt}
        loading="lazy"
        className="h-[112%] w-full object-cover"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-carbon/40 to-transparent" />
    </div>
  );
}

export function Fleet() {
  const { t } = useLang();
  return (
    <section id="flotta" data-testid="fleet-section" className="relative bg-carbon py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <Overline>{t.fleet.overline}</Overline>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-ice sm:text-4xl lg:text-5xl">
            {t.fleet.title}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-steel">{t.fleet.subtitle}</p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {t.fleet.cards.map((card, i) => (
            <Reveal key={card.t} delay={i * 0.12}>
              <div className="overflow-hidden rounded-xl border border-edge bg-asphalt">
                <div className="h-72 md:h-80">
                  <ParallaxImage src={i === 0 ? images.truckReal2 : images.vanReal} alt={card.t} />
                </div>
                <div className="p-6">
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-amber">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-2 font-display text-xl font-bold text-ice">{card.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-steel">{card.d}</p>
                  <p className="mt-4 text-xs font-medium uppercase tracking-wide text-steel">
                    <span className="text-amber">→ </span>{card.use}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p className="mt-8 rounded-lg border border-dashed border-edge bg-asphalt/50 p-4 text-center text-xs text-steel">
            ⚠ {t.fleet.note}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
